import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { OrganizationTypeahead } from '../common';
import api from '../../lib/api';
import type { OrganizationSearchResult, SportWithTeams, Gender, Season } from '../../types';
import toast from 'react-hot-toast';
import { formatSportLabel } from '../../lib/sportLabel';
import { compareLevels } from '../../lib/levelRank';

interface LinkOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  personId: string;
  personName: string;
  existingOrgIds: string[]; // IDs of already-linked organizations to exclude
  onSuccess: () => void; // Called after successful link
}

// Role suggestions
const ROLE_SUGGESTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Athletic Director',
  'Associate AD',
  'Coordinator',
  'Booster Club President',
  'Team Manager',
];

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'BOYS', label: 'Boys' },
  { value: 'GIRLS', label: 'Girls' },
  { value: 'COED', label: 'Co-Ed' },
];

const SEASON_OPTIONS: { value: Season; label: string }[] = [
  { value: 'FALL', label: 'Fall' },
  { value: 'WINTER', label: 'Winter' },
  { value: 'SPRING', label: 'Spring' },
  { value: 'SUMMER', label: 'Summer' },
];

/**
 * Modal for linking a person to an organization with optional sport/team selection
 */
export const LinkOrganizationModal = ({
  isOpen,
  onClose,
  personId,
  personName,
  existingOrgIds,
  onSuccess,
}: LinkOrganizationModalProps) => {
  // Form state
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSearchResult | null>(null);
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sport/Team cascading state
  const [sports, setSports] = useState<SportWithTeams[]>([]);
  const [isLoadingSports, setIsLoadingSports] = useState(false);
  const [selectedSportId, setSelectedSportId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  // Inline add sport
  const [showAddSport, setShowAddSport] = useState(false);
  const [newSportName, setNewSportName] = useState('');
  const [newSportGender, setNewSportGender] = useState<Gender>('BOYS');
  const [newSportSeason, setNewSportSeason] = useState<Season>('FALL');
  const [isCreatingSport, setIsCreatingSport] = useState(false);

  // Inline add team
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamLevel, setNewTeamLevel] = useState('');
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOrg(null);
      setRole('');
      setIsPrimary(false);
      setError(null);
      setSports([]);
      setSelectedSportId('');
      setSelectedTeamId('');
      setShowAddSport(false);
      setShowAddTeam(false);
    }
  }, [isOpen]);

  // Fetch sports when org changes
  useEffect(() => {
    if (!selectedOrg) {
      setSports([]);
      setSelectedSportId('');
      setSelectedTeamId('');
      return;
    }

    const fetchSports = async () => {
      setIsLoadingSports(true);
      try {
        const response = await api.get(`/api/organizations/${selectedOrg.id}/sports`);
        setSports(response.data.sports || []);
      } catch {
        setSports([]);
      } finally {
        setIsLoadingSports(false);
      }
    };

    fetchSports();
  }, [selectedOrg]);

  // Reset team when sport changes
  useEffect(() => {
    setSelectedTeamId('');
    setShowAddTeam(false);
  }, [selectedSportId]);

  const selectedSport = sports.find((s) => s.id === selectedSportId);
  const availableTeams = (selectedSport?.teams.filter((t) => t.isActive) || [])
    .sort((a, b) => compareLevels(a.level, b.level));

  // Inline create sport
  const handleCreateSport = async () => {
    if (!selectedOrg || !newSportName.trim()) return;

    setIsCreatingSport(true);
    try {
      const response = await api.post(
        `/api/organizations/${selectedOrg.id}/sports`,
        { name: newSportName.trim(), gender: newSportGender, season: newSportSeason }
      );
      const newSport = response.data.sport;
      // Add to local sports list and select it
      setSports((prev) => [...prev, { ...newSport, people: [], teams: newSport.teams || [] }]);
      setSelectedSportId(newSport.id);
      setShowAddSport(false);
      setNewSportName('');
      toast.success(`Created ${formatSportLabel(newSportGender, newSportName.trim())}`);
    } catch (err: any) {
      const msg = err.response?.status === 409
        ? 'This sport already exists'
        : (err.response?.data?.message || 'Failed to create sport');
      toast.error(msg);
    } finally {
      setIsCreatingSport(false);
    }
  };

  // Inline create team
  const handleCreateTeam = async () => {
    if (!selectedOrg || !selectedSportId || !newTeamLevel.trim()) return;

    setIsCreatingTeam(true);
    try {
      const response = await api.post(
        `/api/organizations/${selectedOrg.id}/sports/${selectedSportId}/teams`,
        { level: newTeamLevel.trim() }
      );
      const newTeam = response.data.team;
      // Add to local sport's teams and select it
      setSports((prev) =>
        prev.map((s) =>
          s.id === selectedSportId
            ? { ...s, teams: [...s.teams, { ...newTeam, isActive: true, people: newTeam.people || [] }] }
            : s
        )
      );
      setSelectedTeamId(newTeam.id);
      setShowAddTeam(false);
      setNewTeamLevel('');
      toast.success(`Created ${newTeamLevel.trim()} team`);
    } catch (err: any) {
      const msg = err.response?.status === 409
        ? 'This team level already exists'
        : (err.response?.data?.message || 'Failed to create team');
      toast.error(msg);
    } finally {
      setIsCreatingTeam(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.post(`/api/people/${personId}/organizations`, {
        organizationId: selectedOrg.id,
        sportId: selectedSportId || null,
        teamId: selectedTeamId || null,
        role: role.trim() || null,
        isPrimary,
      });

      toast.success(`Linked to ${selectedOrg.name}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to link organization';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Link Organization
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Person name */}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Linking: <span className="font-medium text-gray-900 dark:text-gray-100">{personName}</span>
              </p>

              {/* Error banner */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Organization search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization <span className="text-red-500">*</span>
                </label>
                <OrganizationTypeahead
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  placeholder="Search by school name..."
                  showStateFilter={true}
                  excludeIds={existingOrgIds}
                  disabled={isSaving}
                />
              </div>

              {/* Sport dropdown (shown when org is selected) */}
              {selectedOrg && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sport <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  {isLoadingSports ? (
                    <div className="input flex items-center text-gray-400 text-sm">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></span>
                      Loading sports...
                    </div>
                  ) : showAddSport ? (
                    <div className="space-y-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <input
                        type="text"
                        value={newSportName}
                        onChange={(e) => setNewSportName(e.target.value)}
                        className="input text-sm"
                        placeholder="Sport name (e.g., Football)"
                        disabled={isCreatingSport}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={newSportGender}
                          onChange={(e) => setNewSportGender(e.target.value as Gender)}
                          className="input text-sm"
                          disabled={isCreatingSport}
                        >
                          {GENDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <select
                          value={newSportSeason}
                          onChange={(e) => setNewSportSeason(e.target.value as Season)}
                          className="input text-sm"
                          disabled={isCreatingSport}
                        >
                          {SEASON_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleCreateSport}
                          disabled={isCreatingSport || !newSportName.trim()}
                          className="btn-primary text-xs py-1 px-3"
                        >
                          {isCreatingSport ? 'Creating...' : 'Create'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddSport(false)}
                          className="btn-secondary text-xs py-1 px-3"
                          disabled={isCreatingSport}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={selectedSportId}
                        onChange={(e) => setSelectedSportId(e.target.value)}
                        className="input flex-1"
                        disabled={isSaving}
                      >
                        <option value="">-- No sport (org level) --</option>
                        {sports.filter((s) => s.isActive).map((sport) => (
                          <option key={sport.id} value={sport.id}>
                            {formatSportLabel(sport.gender, sport.name)} ({sport.season.charAt(0) + sport.season.slice(1).toLowerCase()})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddSport(true)}
                        className="btn-secondary text-sm py-2 px-2.5 flex-shrink-0"
                        title="Add new sport"
                        disabled={isSaving}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Team dropdown (shown when sport is selected) */}
              {selectedOrg && selectedSportId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Team <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  {showAddTeam ? (
                    <div className="space-y-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <input
                        type="text"
                        value={newTeamLevel}
                        onChange={(e) => setNewTeamLevel(e.target.value)}
                        className="input text-sm"
                        placeholder="Team level (e.g., Varsity)"
                        disabled={isCreatingTeam}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleCreateTeam}
                          disabled={isCreatingTeam || !newTeamLevel.trim()}
                          className="btn-primary text-xs py-1 px-3"
                        >
                          {isCreatingTeam ? 'Creating...' : 'Create'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddTeam(false)}
                          className="btn-secondary text-xs py-1 px-3"
                          disabled={isCreatingTeam}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={selectedTeamId}
                        onChange={(e) => setSelectedTeamId(e.target.value)}
                        className="input flex-1"
                        disabled={isSaving}
                      >
                        <option value="">-- No team (sport level) --</option>
                        {availableTeams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.level}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddTeam(true)}
                        className="btn-secondary text-sm py-2 px-2.5 flex-shrink-0"
                        title="Add new team"
                        disabled={isSaving}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  id="role"
                  list="role-suggestions"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="input"
                  placeholder="e.g., Head Coach"
                  disabled={isSaving}
                />
                <datalist id="role-suggestions">
                  {ROLE_SUGGESTIONS.map((r) => (
                    <option key={r} value={r} />
                  ))}
                </datalist>
              </div>

              {/* Set as Primary */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPrimary"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  disabled={isSaving}
                  className="h-4 w-4 text-blast-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blast-500"
                />
                <label htmlFor="isPrimary" className="text-sm text-gray-700 dark:text-gray-300">
                  Set as primary organization
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 ml-6">
                The primary organization appears in the people list view
              </p>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !selectedOrg}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Linking...
                  </>
                ) : (
                  'Link Organization'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
